import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import classes from "./BookingDetailsMain.module.css";
import { hasBookingStartPassed } from "../../../util/bookingDateTime";
import dropdownStyles from "../../../pages/StudioProfile/Dropdowns.module.css";
import { SmallModal } from "../../UI/SmallModal/SmallModal";
// import EquipmentCarousel from "./EquipmentCarousel";
import ChatLink from "../../ChatLink";
import { useBookingUpdate } from '../../../hooks/useBookingUpdate';
// import { useCreateHostStudioReviews } from '../../../hooks/reviews/useCreateHostStudioReviews';
// import { useCreateUserReviews } from '../../../hooks/reviews/useCreateUserReviews';
import { useNavigate } from "react-router-dom";
import BookingDetailsMainSkeleton from './BookingDetailsMainSkeleton';
import { NavBarContext } from '../../../App';
import SaveNotification from "../../UI/SaveNotification/SaveNotification";
import { markBookingAsViewed } from '../../../util/bookingViewedTracker';
import { useSelector } from 'react-redux';
import noUserImage from "../../../public/fakeUser/noUser.webp";
import { resolvePaidEquipmentImageSrc, formatPricePerPiece } from "../../../pages/StudioProfile/paidEquipmentHelpers";
import { notifyErrorToast } from '../../../util/notifyActionError';

const HOUSE_RULE_COPY = {
    smokingAllowed: {
        allowed: "Rauchen & Dampfen erlaubt",
        blocked: "Rauchen & Dampfen nicht erlaubt",
    },
    alcoholAllowed: {
        allowed: "Alkoholkonsum erlaubt",
        blocked: "Alkoholkonsum nicht erlaubt",
    },
    foodAllowed: {
        allowed: "Essen im Studiobereich erlaubt",
        blocked: "Essen im Studiobereich nicht erlaubt",
    },
    adultContentAllowed: {
        allowed: "Erwachsenen-Inhalte oder Nacktdrehs erlaubt",
        blocked: "Erwachsenen-Inhalte oder Nacktdrehs nicht erlaubt",
    },
    fogMachineAllowed: {
        allowed: "Nebelmaschinen erlaubt",
        blocked: "Nebelmaschinen nicht erlaubt",
    },
    glitterConfettiAllowed: {
        allowed: "Glitzer, Konfetti oder kleine Partikel erlaubt",
        blocked: "Glitzer, Konfetti oder kleine Partikel nicht erlaubt",
    },
    pyrotechnicsAllowed: {
        allowed: "Flammen & Pyrotechnik erlaubt",
        blocked: "Flammen & Pyrotechnik nicht erlaubt",
    },
    equipmentChangesAllowed: {
        allowed: "Änderungen an der Studio-Ausstattung erlaubt",
        blocked: "Änderungen an der Studio-Ausstattung nicht erlaubt",
    },
};

const CANCELLATION_POLICY_COPY = {
    FLEXIBLE: {
        title: "Kulant",
        lines: [
            { prefix: "Bis 48 Stunden vor Beginn: ", highlight: "Volle Rückerstattung" },
            { prefix: "Bis 24 Stunden vor Beginn: ", highlight: "50% Rückerstattung" },
            { prefix: "Unter 24 Stunden vor Beginn: ", highlight: "Keine Rückerstattung" },
        ],
    },
    STANDARD: {
        title: "Standard",
        lines: [
            { prefix: "Bis 5 Tage vor Beginn: ", highlight: "Volle Rückerstattung" },
            { prefix: "Bis 48 Stunden vor Beginn: ", highlight: "50% Rückerstattung" },
            { prefix: "Unter 48 Stunden vor Beginn: ", highlight: "Keine Rückerstattung" },
        ],
    },
    STRICT: {
        title: "Strikt",
        lines: [
            { prefix: "Bis 14 Tage vor Beginn: ", highlight: "Volle Rückerstattung" },
            { prefix: "Bis 7 Tage vor Beginn: ", highlight: "50% Rückerstattung" },
            { prefix: "Unter 7 Tage vor Beginn: ", highlight: "Keine Rückerstattung" },
        ],
    },
};

const formatHouseRules = (houseRules) => {
    if (!houseRules) return [];
    const rules = Object.entries(HOUSE_RULE_COPY)
        .filter(([key]) => typeof houseRules[key] === "boolean")
        .map(([key, text]) => (houseRules[key] ? text.allowed : text.blocked));
    const custom = houseRules?.customRules?.trim();
    if (custom) rules.push(`Zusätzliche Regeln: ${custom.charAt(0).toUpperCase()}${custom.slice(1)}`);
    return rules;
};

const getCancellationPolicyConfig = (rawValue) => {
    if (!rawValue) return null;
    const trimmed = rawValue.trim();
    const upper = trimmed.toUpperCase();
    if (CANCELLATION_POLICY_COPY[upper]) return CANCELLATION_POLICY_COPY[upper];
    const lower = trimmed.toLowerCase();
    if (lower === "kulant") return CANCELLATION_POLICY_COPY.FLEXIBLE;
    if (lower === "standard") return CANCELLATION_POLICY_COPY.STANDARD;
    if (lower === "strikt") return CANCELLATION_POLICY_COPY.STRICT;
    return null;
};

const TERMINAL_BOOKING_ACTION_STATUSES = new Set([
    "CANCELLED",
    "WITHDRAWN",
    "DECLINED",
    "REJECTED",
    "EXPIRED",
    "COMPLETED",
    "NO_SHOW",
]);

const normalizeBookingStatus = (status) => String(status || "").trim().toUpperCase();

const canShowCancelBookingAction = ({ status, fromCreatorPage, isBookingTimePassed }) => {
    const normalizedStatus = normalizeBookingStatus(status);

    if (TERMINAL_BOOKING_ACTION_STATUSES.has(normalizedStatus) || isBookingTimePassed) {
        return false;
    }

    if (!fromCreatorPage && normalizedStatus === "PENDING") {
        return true;
    }

    return normalizedStatus === "CONFIRMED";
};

// const ReviewTextarea = ({ value, onChange, placeholder }) => {
//     return (
//         <textarea 
//             className={`${classes.reviewTextarea} slimRegularLarge`} 
//             placeholder={placeholder}
//             value={value}
//             onChange={onChange}
//         />
//     );
// };

export const BookingDetailsMain = ({ fromCreatorPage = false, bookingData, packageData = null, loading = false }) => {    
    const { user } = useSelector((state) => state?.auth);
    const userId = user?.id || user?._id;

    const { updateBookingStatus, isLoading: isBookingUpdateLoading } = useBookingUpdate();
    const navigate = useNavigate();
    const { activeView, setActiveView } = useContext(NavBarContext);
    const [open, setOpen] = useState(false);

    const hasBookingTimePassed = () => hasBookingStartPassed(bookingData?.startDateTime);

    // Mark booking as viewed when component mounts or booking data changes
    useEffect(() => {
        if (bookingData?.id && bookingData?.status && userId) {
            markBookingAsViewed(bookingData.id, bookingData.status, userId);
        }
    }, [bookingData?.id, bookingData?.status, userId]);

    // Handle profile navigation while preserving creator mode
    const handleProfileNavigation = () => {
        if (fromCreatorPage || activeView === 'creator') {
            setActiveView('creator');
        }
    };
    const [modalType, setModalType] = useState(null);
    const [isProjectExpanded, setIsProjectExpanded] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isBookingExpanded, setIsBookingExpanded] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [isRulesExpanded, setIsRulesExpanded] = useState(false);
    // const [selectedCommunicationStars, setSelectedCommunicationStars] = useState(0);
    // const [currentPage, setCurrentPage] = useState(1);
    // const [selectedStars, setSelectedStars] = useState(0);
    // const [selectedStudioStars, setSelectedStudioStars] = useState(0);
    const [isPriceExpanded, setIsPriceExpanded] = useState(false);
    // const [showExpandButton, setShowExpandButton] = useState(false);


    // const guestReviewRef = useRef('');
    // const studioReviewRef = useRef('');

    // const [moreReviews, setMoreReviews] = useState(true);
    // const [isRateGuestOpen, setIsRateGuestOpen] = useState(false);
    // const [isRateBookingOpen, setIsRateBookingOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const bookedEquipmentItems = Array.isArray(bookingData?.bookedEquipment) ? bookingData.bookedEquipment : [];
    const studioPaidEquipmentItems = Array.isArray(bookingData?.Studio?.paidEquipmentItems?.items)
        ? bookingData.Studio.paidEquipmentItems.items
        : [];
    const bookedEquipmentCards = bookedEquipmentItems.map((item) => {
        const matchedStudioItem = studioPaidEquipmentItems.find(
            (studioItem) => studioItem?.id === item?.equipmentItemId
        );
        return {
            ...item,
            imageKey: matchedStudioItem?.imageKey || null,
            price: matchedStudioItem?.price,
        };
    });
    const getBookedEquipmentUnitPrice = (equipmentItemId) => {
        const matchedStudioItem = studioPaidEquipmentItems.find((studioItem) => studioItem?.id === equipmentItemId);
        const price = Number(matchedStudioItem?.price);
        return Number.isFinite(price) ? price : null;
    };


    const [text, setText] = useState('');
    const [bookingText, setBookingText] = useState('');
    // const { submitStudioReview, isSubmitting: isStudioReviewSubmitting } = useCreateHostStudioReviews();
    // const { submitUserReview, isSubmitting: isUserReviewSubmitting } = useCreateUserReviews();

    // const [guestReviewText, setGuestReviewText] = useState('');
    // const [studioReviewText, setStudioReviewText] = useState('');

    // const checkIfBookingHasPassed = useMemo(() => {
    //     const currentTime = new Date();
    //     const bookingTime = new Date(bookingData?.startDateTime);
    //     setBookingHasPassed(currentTime > bookingTime);
    // }, [bookingData]);

    // Helper function to format booking details for first message
    // const formatBookingDetailsMessage = (bookingData, fromCreatorPage) => {
    //     if (!bookingData) return "";
        
    //     const startDate = new Date(bookingData.startDateTime).toLocaleDateString('de-DE', {
    //         weekday: 'long',
    //         year: 'numeric',
    //         month: 'long',
    //         day: 'numeric'
    //     });
    //     const startTime = new Date(bookingData.startDateTime).toLocaleTimeString('de-DE', {
    //         hour: '2-digit',
    //         minute: '2-digit'
    //     });
    //     const endTime = new Date(bookingData.endDateTime).toLocaleTimeString('de-DE', {
    //         hour: '2-digit',
    //         minute: '2-digit'
    //     });
        
    //     const studioName = fromCreatorPage ? bookingData.Studio?.name : bookingData.Studio?.StudioName;
    //     const greeting = fromCreatorPage ? "Hallo! Ich schreibe bezüglich der Buchung:" : "Hallo! Ich schreibe bezüglich meiner Buchung:";
        
    //     return `${greeting}\n\n📅 ${startDate}\n⏰ ${startTime} - ${endTime}\n🏢 ${studioName}\n💰 €${bookingData.totalPrice}\n\nProjekt: ${bookingData.projectName}`;
    // };

    const calculateBookingDuration = (startDateTime, endDateTime) => {
        if (!startDateTime || !endDateTime) return 0;
        const start = new Date(startDateTime);
        const end = new Date(endDateTime);
        const durationMs = end.getTime() - start.getTime();
        const durationHours = durationMs / (1000 * 60 * 60);
        return Math.round(durationHours * 100) / 100;
    };

    const handleConfirmBooking = async () => {
        try {
            await updateBookingStatus(bookingData?.id, "CONFIRMED");
            setNotificationMessage('Buchung bestätigt');
            setShowNotification(true);
            handleClose(); // Close modal after success
        } catch (error) {
            notifyErrorToast('Es gab ein Problem beim Bestätigen der Buchung. Bitte versuche es später erneut.', {
              title: 'Buchung konnte nicht bestätigt werden',
              onRetry: handleConfirmBooking,
            });
        }
    };

    // Format duration for display (e.g., "2 Stunden")
    const formatBookingDuration = (startDateTime, endDateTime) => {
        if (!startDateTime || !endDateTime) return "0 Stunden";
        const duration = calculateBookingDuration(startDateTime, endDateTime);
        const hours = Math.round(duration); // Round to nearest hour since only hours are used
        return `${hours} Stunden`;
    };

    const formatPrice = (value) => {
        const num = Number(value) || 0;
        if (Number.isInteger(num)) return String(num);
        return String(num).replace('.', ',');
    };

    // Reconstruct base studio price from stored totals and adjustments
    // Backend stores totalStudioPrice as (base - discount); extra fees are separate in appliedFees
    const calculateBaseStudioPrice = () => {
        if (bookingData?.totalStudioPrice == null) return null;

        const discount = Number(bookingData?.appliedDiscount) || 0;
        const surcharge = Number(bookingData?.appliedSurcharge) || 0;

        const base = Number(bookingData.totalStudioPrice) + discount - surcharge;
        return Math.max(0, base);
    };

    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 600);
        };

        checkIfMobile();

        window.addEventListener('resize', checkIfMobile);

        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // const submitReview = async () => {
    //     try {
    //         if (!isRateBookingOpen) {
    //             // User review (host reviewing guest)
    //             const reviewData = {
    //                 rating: selectedStars,
    //                 content: guestReviewText || "",
    //                 reviewerId: bookingData?.userID,
    //                 revieweeId: bookingData?.Studio?.User?.id,
    //                 studioId: bookingData?.Studio?.id,
    //                 bookingId: bookingData?.id,
    //             };
    //             await submitUserReview(reviewData);
    //         } else {
    //             // Studio review (guest reviewing studio/host)
    //             await submitStudioReview({
    //                 rating: selectedStudioStars,
    //                 communicationRating: selectedCommunicationStars,
    //                 content: studioReviewText || "",
    //                 reviewerId: bookingData?.userID,
    //                 revieweeId: bookingData?.Studio?.User?.id,
    //                 studioId: bookingData?.Studio?.id,
    //                 bookingId: bookingData?.id
    //             });
    //         }
    //         setCurrentPage(3);
    //     } catch (error) {
    //         if (error.message && error.message.includes("already submitted a review")) {
    //             alert("Du hast bereits eine Bewertung abgegeben.");
    //         } else {
    //             alert("Es gab ein Problem beim Absenden deiner Bewertung. Bitte versuche es später erneut.");
    //         }
    //     }
    // };
    
    const houseRulesList = formatHouseRules(bookingData?.Studio?.houseRules);
    const visibleRules = isRulesExpanded ? houseRulesList : houseRulesList.slice(0, 2);
    const cancellationPolicyRaw = bookingData?.Studio?.cancellationPolicy || "";
    const cancellationPolicyConfig = getCancellationPolicyConfig(cancellationPolicyRaw);
    
    // const handleStarClick = (starIndex) => {
    //     setSelectedStars(starIndex + 1);
    // };

    // const handleStudioStarClick = (starIndex) => {
    //     setSelectedStudioStars(starIndex + 1);
    // };

    // const handleCommunicationStarClick = (starIndex) => {
    //     setSelectedCommunicationStars(starIndex + 1);
    // };

    // const handleNextClick = () => {
    //     if (isRateBookingOpen) {
    //         if (selectedStudioStars > 0 && selectedCommunicationStars > 0) {
    //             setCurrentPage(2);
    //         }
    //     } else {
    //         if (selectedStars > 0) {
    //             setCurrentPage(2);
    //         }
    //     }
    // };

    const toggleRules = () => {
        setIsRulesExpanded(!isRulesExpanded);
    };

    useEffect(() => {
        const cancellationPolicy = bookingData?.Studio?.cancellationPolicy || '';
        const description = bookingData?.projectDescription || '';
        
        const characterLimit = isMobile ? 90 : 150;
        
        setText(isExpanded ? cancellationPolicy : 
            cancellationPolicy.length > characterLimit ? 
                `${cancellationPolicy.slice(0, characterLimit)}...` : 
                cancellationPolicy);
        
        setBookingText(
            isBookingExpanded ? description : 
                description.length > characterLimit ? 
                    `${description.slice(0, characterLimit)}...` : 
                    description);
    }, [isExpanded, isBookingExpanded, bookingData, isMobile]);

    useEffect(() => {
        const description = bookingData?.projectDescription || '';
        const characterLimit = isMobile ? 90 : 150;
        
        setBookingText(
            isProjectExpanded ? description : 
                description.length > characterLimit ? 
                    `${description.slice(0, characterLimit)}...` : 
                    description);
    }, [isProjectExpanded, bookingData, isMobile]);
    

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const toggleBookingExpand = () => {
        setIsBookingExpanded(!isBookingExpanded);
    };

    const toggleProjectExpand = () => {
        setIsProjectExpanded(!isProjectExpanded);
    };

    const handleReject = async () => {
        try {
            await updateBookingStatus(bookingData?.id, "DECLINED");
            setNotificationMessage('Buchung abgelehnt');
            setShowNotification(true);
            handleClose();
            setTimeout(() => {
                navigate("/creator");
            }, 1500);
        } catch (error) {
            notifyErrorToast('Es gab ein Problem beim Ablehnen der Buchung. Bitte versuche es später erneut.', {
              title: 'Buchung konnte nicht abgelehnt werden',
              onRetry: handleReject,
            });
        }
    };

    // const handleRateGuest = () => {
    //     setOpen(true);
    //     setIsRateGuestOpen(true);
    //     setIsRateBookingOpen(false);
    // };

    // const handleRateBooking = () => {
    //     setOpen(true);
    //     setIsRateGuestOpen(true);
    //     setIsRateBookingOpen(true);
    // };

    const handleOpenModal = (type) => {
        setOpen(true);
        setModalType(type);
    }
    
    const handleAccept = async () => {
        await handleConfirmBooking();
        // handleClose will be called in handleConfirmBooking after success
    };

    const handleClose = () => {
        // setSelectedCommunicationStars(0);
        // setCurrentPage(1);
        // setSelectedStars(0);
        // setSelectedStudioStars(0);
        // setGuestReviewText('');
        // setStudioReviewText('');
        // guestReviewRef.current = '';
        // studioReviewRef.current = '';
        setOpen(false);      
        // setIsRateBookingOpen(false);
        // setIsRateGuestOpen(false);
        setModalType(null);
    };

    const togglePriceExpand = () => {
        setIsPriceExpanded(!isPriceExpanded);
    };  

    const handleCancelBooking = async () => {
        const bookingStatus = normalizeBookingStatus(bookingData?.status);
        const isBookingTimePassed = hasBookingTimePassed();
        const canSubmitCancelBookingAction = canShowCancelBookingAction({
            status: bookingStatus,
            fromCreatorPage,
            isBookingTimePassed,
        });

        if (!canSubmitCancelBookingAction) {
            handleClose();
            return;
        }

        try {
            // If guest mode and booking is PENDING, withdraw instead of cancel
            const isWithdrawal = !fromCreatorPage && bookingStatus === "PENDING";
            const newStatus = isWithdrawal ? "WITHDRAWN" : "CANCELLED";
            
            await updateBookingStatus(bookingData?.id, newStatus);
            setNotificationMessage(
                isWithdrawal
                    ? 'Anfrage zurückgezogen'
                    : 'Buchung storniert'
            );
            setShowNotification(true);
            handleClose(); // Close modal after success
            setTimeout(() => {
                if (fromCreatorPage) {
                    navigate("/creator");
                } else {
                    navigate("/bookings");
                }
            }, 1500); // Navigate after showing notification briefly
        } catch (error) {
            const isWithdrawal = !fromCreatorPage && bookingStatus === "PENDING";
            notifyErrorToast(
              isWithdrawal
                ? 'Es gab ein Problem beim Zurückziehen der Anfrage. Bitte versuche es später erneut.'
                : 'Es gab ein Problem beim Stornieren der Buchung. Bitte versuche es später erneut.',
              {
                title: isWithdrawal ? 'Anfrage konnte nicht zurückgezogen werden' : 'Buchung konnte nicht storniert werden',
                onRetry: handleCancelBooking,
              }
            );
        }
    };
    
    const openCancelBookingModal = () => {
        const canOpenCancelBookingAction = canShowCancelBookingAction({
            status: bookingData?.status,
            fromCreatorPage,
            isBookingTimePassed: hasBookingTimePassed(),
        });

        if (!canOpenCancelBookingAction) {
            handleClose();
            return;
        }

        setModalType('cancelBooking');
        setOpen(true);
    };

    // const SmallModalGuestContent = () => {
    //     const { submitUserReview, isSubmitting: isUserReviewSubmitting } = useCreateUserReviews();
        
    //     const handleGuestReviewSubmit = async () => {
    //         try {
    //             const reviewData = {
    //                 rating: selectedStars,
    //                 content: guestReviewText || "",
    //                 reviewerId: bookingData?.userID,
    //                 revieweeId: bookingData?.Studio?.User?.id,
    //                 studioId: bookingData?.Studio?.id,
    //                 bookingId: bookingData?.id,
    //             };
                
    //             await submitUserReview(reviewData);
    //             setCurrentPage(3);
    //         } catch (error) {
    //             if (error.message && error.message.includes("already submitted a review")) {
    //                 alert("Du hast bereits eine Bewertung für diesen Gast abgegeben.");
    //             } else {
    //                 alert("Es gab ein Problem beim Absenden deiner Bewertung. Bitte versuche es später erneut.");
    //             }
    //         }
    //     };

    //     return (
    //         <div className={classes.reviewsContainer}>
    //             <header className={classes.reviewsHeader}>
    //                 <h2 className={`titleSemibold3 ${classes.reviewsTitle}`}>Bewerte deinen Host</h2>
    //                 <button onClick={handleClose} className={classes.reviewsCloseButton}>
    //                     <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    //                         <path fillRule="evenodd" clipRule="evenodd" d="M7.62275 8.77149L0.113281 1.26202L1.10382 0.271484L8.61328 7.78095L16.1227 0.271484L17.1133 1.26202L9.60382 8.77149L17.1133 16.281L16.1227 17.2715L8.61328 9.76202L1.10382 17.2715L0.113281 16.281L7.62275 8.77149Z" fill="#1F1F1F"/>
    //                     </svg>
    //                 </button>
    //             </header>
    //             <div className={classes.pageIndicatorContainer}>
    //                 <div style={{backgroundColor: 'var(--stu-rot)'}} className={classes.pageIndicator}></div>
    //                 <div style={{backgroundColor: currentPage >= 2 ? 'var(--stu-rot)' : ''}} className={classes.pageIndicator}></div>
    //             </div>
    //             {currentPage === 1 ? (
    //                 <div className={classes.reviewsInnerContainer}>
    //                     <div className={classes.userContainer}>
    //                         <div className={classes.userImageContainer}>
    //                             <img src={bookingData?.Studio?.User?.studioOwnerProfilePicture} alt="user" />
    //                         </div>
    //                         <div className={classes.userInfoContainer}>
    //                             <h2 className="titleSemibold2">{bookingData?.Studio?.User?.firstName} {bookingData?.Studio?.User?.lastName}</h2>
    //                             <p className="regularLarge">{bookingData?.Studio?.studioLocation}</p>
    //                         </div>
    //                     </div>
    //                     <div className={classes.longLine}></div>
    //                     <div className={classes.startReviewContainer}>
    //                         <h2 className="titleSemibold2">Bewerte deinen Host.</h2>
    //                         <div className={classes.starsContainer}>
    //                             {[...Array(5)].map((_, index) => (
    //                                 <svg 
    //                                     key={index}
    //                                     onClick={() => handleStarClick(index)}
    //                                     style={{cursor: 'pointer'}}
    //                                     xmlns="http://www.w3.org/2000/svg" 
    //                                     width="42" 
    //                                     height="40" 
    //                                     viewBox="0 0 42 40"
    //                                     fill="none"
    //                                 >
    //                                     <g clipPath="url(#clip0_10926_15309)">
    //                                         <path d="M32.2487 29.5767L33.9688 39.6587L20.9961 32.8737L31.508 25.3003L32.0097 28.1911L32.2487 29.5767Z" fill={index < selectedStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
    //                                         <path d="M9.98635 28.1911L10.4881 25.3003L0 15.1468L14.5017 13.0444L21 0L27.4983 13.0444L42 15.1468L8.0273 39.6587L9.98635 28.1911Z" fill={index < selectedStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
    //                                     </g>
    //                                     <defs>
    //                                         <clipPath id="clip0_10926_15309">
    //                                             <rect width="42" height="39.6587" fill="white"/>
    //                                         </clipPath>
    //                                     </defs>
    //                                 </svg>
    //                             ))}
    //                         </div>
    //                     </div>
    //                 </div>
    //             ) : currentPage === 2 ? (
    //                 <div className={classes.reviewsInnerContainerSecond}>
    //                     <h2 className="titleSemibold2">Teile deine Erfahrung mit {bookingData?.Studio?.User?.firstName} (optional)</h2>
    //                     <textarea 
    //                         className={`${classes.reviewTextarea} slimRegularLarge`} 
    //                         placeholder={`Beschreibe deine Erfahrung mit ${bookingData?.Studio?.User?.firstName} hier...`}
    //                         defaultValue={guestReviewText}
    //                         onBlur={(e) => setGuestReviewText(e.target.value)}
    //                     ></textarea>
    //                 </div>
    //             ) : (
    //                 <>
    //                 <div className={classes.reviewsInnerContainerSuccess} style={{paddingBottom: !moreReviews ? '125px' : '52px'}}>
    //                     <div className={classes.successContent}>
    //                         <div className={classes.successContentIcon}>
    //                             <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24" fill="none">
    //                                 <path fillRule="evenodd" clipRule="evenodd" d="M31.6074 2.63723L9.69105 23.4831L9.69062 23.4827L9.68855 23.4847L0.392578 13.9887L2.48517 11.9402L9.76228 19.3739L29.5892 0.515381L31.6074 2.63723Z" fill="white"/>
    //                             </svg>
    //                         </div>
    //                         <h2 className={`${classes.successContentTitle} titleSemibold2`}>Deine Bewertung wurde abgeschickt!</h2>
    //                     </div>
    //                     {/* {moreReviews && (
    //                         <button className={`${classes.moreReviewsButton} mediumLarge`}>
    //                             Bewertung über dich anschauen
    //                         </button>
    //                     )} */}
    //                 </div>
    //                 {/* {moreReviews && (
    //                     <button className={`${classes.moreReviewsButtonPhone} mediumLarge`}>
    //                         Bewertung über dich anschauen
    //                     </button>
    //                 )} */}
    //                 </>
    //             )}
    //             <footer className={classes.footerContainer} style={{justifyContent: currentPage === 3 || isMobile ? 'flex-end' : 'space-between'}}>
    //                 {currentPage !== 3 && (
    //                     <button 
    //                         className={`${classes.backButton} mediumLarge`}
    //                         onClick={() => currentPage === 2 && setCurrentPage(1)}
    //                     >
    //                         <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
    //                             <line x1="9.60104" y1="0.726041" x2="0.602492" y2="9.72459" stroke="#1F1F1F" strokeWidth="1.7"/>
    //                             <line x1="0.626536" y1="8.547" x2="9.62508" y2="18.2954" stroke="#1F1F1F" strokeWidth="1.7"/>
    //                         </svg>
    //                         Zurück
    //                     </button>
    //                 )}
    //                 <button 
    //                     className={`${classes.nextButton} mediumLarge`}
    //                     onClick={currentPage === 2 ? handleGuestReviewSubmit : currentPage === 3 ? handleClose : handleNextClick}
    //                     disabled={(currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting}
    //                     style={{
    //                         width: currentPage === 3 && isMobile ? '100%' : 'auto',
    //                         opacity: (currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting ? 0.5 : 1,
    //                         cursor: (currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting ? 'not-allowed' : 'pointer'
    //                     }}
    //                 >
    //                     {currentPage === 1 ? 'Weiter' : currentPage === 2 ? (isUserReviewSubmitting ? 'Wird gesendet...' : 'Abschicken') : 'Fertig'}
    //                 </button>
    //             </footer>
    //         </div>
    //     )
    // }

    // const SmallModalBookingContent = () => {

    //     const { submitStudioReview, isSubmitting: isStudioReviewSubmitting } = useCreateHostStudioReviews();

    //     const handleBookingReviewSubmit = async () => {
    //         try {
    //             const reviewData = {
    //                 rating: selectedStudioStars,
    //                 communicationRating: selectedCommunicationStars,
    //                 content: studioReviewText || "",
    //                 reviewerId: bookingData?.userID,
    //                 revieweeId: bookingData?.Studio?.User?.id,
    //                 studioId: bookingData?.Studio?.id,
    //                 bookingId: bookingData?.id
    //             };

    //             await submitStudioReview(reviewData);
    //             setCurrentPage(3);
    //         } catch (error) {
    //             console.error('Error submitting review:', error);
    //             if (error.message && error.message.includes("already submitted a review")) {
    //                 alert("Du hast bereits eine Bewertung für diese Buchung abgegeben.");
    //             } else {
    //                 alert("Es gab ein Problem beim Absenden deiner Bewertung. Bitte versuche es später erneut.");
    //             }
    //         }
    //     };

    //     return (
    //         <div className={classes.reviewsContainer}>
    //             <header className={classes.reviewsHeader}>
    //                 <h2 className={`titleSemibold3 ${classes.reviewsTitle}`}>Bewerte deinen Buchung</h2>
    //                 <button onClick={handleClose} className={classes.reviewsCloseButton}>
    //                     <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    //                         <path fillRule="evenodd" clipRule="evenodd" d="M7.62275 8.77149L0.113281 1.26202L1.10382 0.271484L8.61328 7.78095L16.1227 0.271484L17.1133 1.26202L9.60382 8.77149L17.1133 16.281L16.1227 17.2715L8.61328 9.76202L1.10382 17.2715L0.113281 16.281L7.62275 8.77149Z" fill="#1F1F1F"/>
    //                     </svg>
    //                 </button>
    //             </header>
    //             <div className={classes.pageIndicatorContainer}>
    //                 <div style={{backgroundColor: 'var(--stu-rot)'}} className={classes.pageIndicator}></div>
    //                 <div style={{backgroundColor: currentPage >= 2 ? 'var(--stu-rot)' : ''}} className={classes.pageIndicator}></div>
    //             </div>
    //             {currentPage === 1 ? (
    //                 <div className={classes.reviewsInnerContainer}>
    //                     <div className={classes.userContainer}>
    //                         <div className={classes.bookingImageContainer}>
    //                             <img className={classes.bookingImage} src={bookingData?.Studio?.processedImages?.[0]} alt="user" />
    //                             <div className={classes.bookingImageContainerInner}>
    //                                 <img className={classes.userImageBookingRating} src={bookingData?.Studio?.User?.studioOwnerProfilePicture} alt="user" />
    //                             </div>
    //                         </div>
    //                         <div className={classes.userInfoContainer}>
    //                             <h2 className="titleSemibold3">{bookingData?.Studio?.StudioName}</h2>
    //                             <h2 className="semiboldLarge">Host: {bookingData?.Studio?.User?.firstName} {bookingData?.Studio?.User?.lastName}</h2>
    //                             <p className="regularLarge">{bookingData?.Studio?.studioLocation}</p>
    //                         </div>
    //                     </div>
    //                     <div className={classes.longLine}></div>
    //                     <div className={classes.startReviewContainer}>
    //                         <h2 className="titleSemibold2">Wie war das Studio & die Ausstattung?</h2>
    //                         <div className={classes.starsContainer}>
    //                             {[...Array(5)].map((_, index) => (
    //                                 <svg 
    //                                     key={index}
    //                                     onClick={() => handleStudioStarClick(index)}
    //                                     style={{cursor: 'pointer'}}
    //                                     xmlns="http://www.w3.org/2000/svg" 
    //                                     width="42" 
    //                                     height="40" 
    //                                     viewBox="0 0 42 40"
    //                                     fill="none"
    //                                 >
    //                                     <g clipPath="url(#clip0_10926_15309)">
    //                                         <path d="M32.2487 29.5767L33.9688 39.6587L20.9961 32.8737L31.508 25.3003L32.0097 28.1911L32.2487 29.5767Z" fill={index < selectedStudioStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
    //                                         <path d="M9.98635 28.1911L10.4881 25.3003L0 15.1468L14.5017 13.0444L21 0L27.4983 13.0444L42 15.1468L8.0273 39.6587L9.98635 28.1911Z" fill={index < selectedStudioStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
    //                                     </g>
    //                                     <defs>
    //                                         <clipPath id="clip0_10926_15309">
    //                                             <rect width="42" height="39.6587" fill="white"/>
    //                                         </clipPath>
    //                                     </defs>
    //                                 </svg>
    //                             ))}
    //                         </div>
    //                         <h2 className="titleSemibold2">Wie war die Kommunikation mit {bookingData?.Studio?.User?.firstName}?</h2>
    //                         <div className={classes.starsContainer}>
    //                             {[...Array(5)].map((_, index) => (
    //                                 <svg 
    //                                     key={index}
    //                                     onClick={() => handleCommunicationStarClick(index)}
    //                                     style={{cursor: 'pointer'}}
    //                                     xmlns="http://www.w3.org/2000/svg" 
    //                                     width="42" 
    //                                     height="40" 
    //                                     viewBox="0 0 42 40"
    //                                     fill="none"
    //                                 >
    //                                     <g clipPath="url(#clip0_10926_15309)">
    //                                         <path d="M32.2487 29.5767L33.9688 39.6587L20.9961 32.8737L31.508 25.3003L32.0097 28.1911L32.2487 29.5767Z" fill={index < selectedCommunicationStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
    //                                         <path d="M9.98635 28.1911L10.4881 25.3003L0 15.1468L14.5017 13.0444L21 0L27.4983 13.0444L42 15.1468L8.0273 39.6587L9.98635 28.1911Z" fill={index < selectedCommunicationStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
    //                                     </g>
    //                                     <defs>
    //                                         <clipPath id="clip0_10926_15309">
    //                                             <rect width="42" height="39.6587" fill="white"/>
    //                                         </clipPath>
    //                                     </defs>
    //                                 </svg>
    //                             ))}
    //                         </div>
    //                     </div>
    //                 </div>
    //             ) : currentPage === 2 ? (
    //                 <div className={classes.reviewsInnerContainerSecond}>
    //                     <h2 className="titleSemibold2">Wie war deine Studio-Erfahrung?</h2>
    //                     <textarea 
    //                         className={`${classes.reviewTextarea} slimRegularLarge`} 
    //                         placeholder="Teile mit anderen wichtige Details über deine Studio-Erfahrung..."
    //                         defaultValue={studioReviewText}
    //                         onBlur={(e) => setStudioReviewText(e.target.value)}
    //                     ></textarea>
    //                 </div>
    //             ) : (
    //                 <>
    //                 <div className={classes.reviewsInnerContainerSuccess} style={{paddingBottom: !moreReviews ? '125px' : '52px'}}>
    //                     <div className={classes.successContent}>
    //                         <div className={classes.successContentIcon}>
    //                             <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24" fill="none">
    //                                 <path fillRule="evenodd" clipRule="evenodd" d="M31.6074 2.63723L9.69105 23.4831L9.69062 23.4827L9.68855 23.4847L0.392578 13.9887L2.48517 11.9402L9.76228 19.3739L29.5892 0.515381L31.6074 2.63723Z" fill="white"/>
    //                             </svg>
    //                         </div>
    //                         <h2 className={`${classes.successContentTitle} titleSemibold2`}>Deine Bewertung wurde abgeschickt!</h2>
    //                     </div>
    //                     <p className="regularLarge">Danke für dein Feedback.</p>
    //                     {/* {moreReviews && (
    //                         <button className={`${classes.moreReviewsButton} mediumLarge`}>
    //                             Bewertung über dich anschauen
    //                         </button>
    //                     )} */}
    //                 </div>
    //                 {/* {moreReviews && (
    //                     <button className={`${classes.moreReviewsButtonPhone} mediumLarge`}>
    //                         Bewertung über dich anschauen
    //                     </button>
    //                 )} */}
    //                 </>
    //             )}
    //             <footer className={classes.footerContainer} style={{justifyContent: currentPage === 3 || isMobile ? 'flex-end' : 'space-between'}}>
    //                 {currentPage !== 3 && (
    //                     <button 
    //                         className={`${classes.backButton} mediumLarge`}
    //                         onClick={() => currentPage === 2 && setCurrentPage(1)}
    //                     >
    //                         <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
    //                             <line x1="9.60104" y1="0.726041" x2="0.602492" y2="9.72459" stroke="#1F1F1F" strokeWidth="1.7"/>
    //                             <line x1="0.626536" y1="8.547" x2="9.62508" y2="18.2954" stroke="#1F1F1F" strokeWidth="1.7"/>
    //                         </svg>
    //                         Zurück
    //                     </button>
    //                 )}
    //                 <button 
    //                     className={`${classes.nextButton} mediumLarge`}
    //                     onClick={currentPage === 2 ? handleBookingReviewSubmit : currentPage === 3 ? handleClose : handleNextClick}
    //                     disabled={isStudioReviewSubmitting || (currentPage === 1 && (selectedStudioStars === 0 || selectedCommunicationStars === 0))}
    //                     style={{
    //                         width: currentPage === 3 && isMobile ? '100%' : 'auto',
    //                         opacity: (currentPage === 1 && (selectedStudioStars === 0 || selectedCommunicationStars === 0)) || isStudioReviewSubmitting ? 0.5 : 1,
    //                         cursor: (currentPage === 1 && (selectedStudioStars === 0 || selectedCommunicationStars === 0)) || isStudioReviewSubmitting ? 'not-allowed' : 'pointer'
    //                     }}
    //                 >
    //                     {currentPage === 1 ? 'Weiter' : currentPage === 2 ? (isStudioReviewSubmitting ? 'Wird gesendet...' : 'Abschicken') : 'Fertig'}
    //                 </button>
    //             </footer>
    //         </div>
    //     )
    // }

    if (loading || !bookingData) {
        return <BookingDetailsMainSkeleton fromCreatorPage={fromCreatorPage} />;
    }

    const bookingStatus = normalizeBookingStatus(bookingData?.status);
    const isBookingTimePassed = hasBookingTimePassed();
    const isGuestPendingWithdrawal = !fromCreatorPage && bookingStatus === "PENDING";
    const showCancelBookingAction = canShowCancelBookingAction({
        status: bookingStatus,
        fromCreatorPage,
        isBookingTimePassed,
    });

  return (
    <>
    <div className={classes.container}>
        {!fromCreatorPage ? (
        <div className={classes.hostContainer}>
            <h2 className={`${classes.hostTitle} titleSemibold3`}>Dein Host</h2>
            <Link 
                to={`/users/show/${bookingData?.Studio?.userID}`}
                onClick={handleProfileNavigation}
            >
                <div className={classes.userProfile}>
                    <div className={classes.userPicture}>
                        <img src={bookingData?.Studio?.User?.studioOwnerProfilePicture || noUserImage} alt='User Profile' />
                    </div>
                    <div className={classes.hostInnerContainer}>
                        <div className={classes.host}>
                            <p className='semiboldLarge'>Studio Host: {bookingData?.Studio?.User?.firstName} {bookingData?.Studio?.User?.lastName}</p>
                            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="15" viewBox="0 0 8 15" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M6.74011 8.26008L7 7.95487L6.74544 7.63101L1.68535 1.19336L1 1.80438L5.81471 7.92985L1.02575 13.5539L1.68782 14.1934L6.74011 8.26008Z" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.4"/>
                            </svg>
                        </div>
                        <span className='regularMiddle' style={{color: "#696F76"}}>
                            Host
                        </span>
                    </div>
                </div>
            </Link>
            <ChatLink 
                userId={bookingData?.Studio?.userID}
                studioId={bookingData?.Studio?.id}
                studioName={bookingData?.Studio?.StudioName}
                studioImage={bookingData?.Studio?.processedImages?.[0]}
                className={`${classes.contactButton} semiboldXs`}
                chatRoomId={bookingData?.chatRoomID}
                bookingId={bookingData?.id}
            >
                Nachricht schreiben
            </ChatLink>
            {/* <button onClick={handleRateGuest} className={`${classes.contactButton} semiboldSmall`}>Bewerte deinen Host</button>
            <button onClick={handleRateBooking} className={`${classes.contactButton} semiboldSmall`}>Bewerte deine Buchung</button> */}
        </div>
        ) : (
            <div className={classes.hostContainer}>
                <h2 className={`${classes.hostTitle} titleSemibold3`}>Dein Gast</h2>
                <Link 
                    to={`/users/show/${bookingData?.User?.id}`}
                    onClick={handleProfileNavigation}
                >
                <div className={classes.userProfile}>
                    <div className={classes.userPicture}>
                        <img src={bookingData?.User?.userProfilePicture || noUserImage} alt='User Profile' />
                    </div>
                    <div className={classes.hostInnerContainer}>
                        <div className={classes.host}>
                            <span className='semiboldLarge'>{bookingData?.User?.firstName} {bookingData?.User?.lastName}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="15" viewBox="0 0 8 15" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M6.74011 8.26008L7 7.95487L6.74544 7.63101L1.68535 1.19336L1 1.80438L5.81471 7.92985L1.02575 13.5539L1.68782 14.1934L6.74011 8.26008Z" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.4"/>
                            </svg>
                        </div>
                        <span className='regularMiddle' style={{color: "#696F76"}}>
                            Gast
                        </span>
                    </div>
                </div>
                </Link>
                {/* <div className={classes.infosContainer}>
                    <div className={classes.info}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="23" height="21" viewBox="0 0 23 21" fill="none">
                            <path d="M16.5589 14.5762L17.3363 19.1345L11.5 14.9064L5.66368 19.1345L6.44114 14.5762L6.69675 13.1065L6.73935 12.8296L6.537 12.6379L2 8.22868L8.28363 7.31276L8.56054 7.27016L8.67769 7.0252L11.5 1.34863L14.3223 7.0252L14.4395 7.27016L14.7164 7.31276L21 8.22868L16.463 12.6379L16.2607 12.8296L16.3033 13.1065L16.5589 14.5762Z" stroke="#1F1F1F"/>
                        </svg>
                        <span className={`regularLarge ${classes.underlined}`}>Bewertungen: Keine Bewertung</span>
                    </div>
                    <div className={classes.info}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none">
                            <path d="M13.5 7.82655L13.3865 7.94164L8.02888 13.4742L5.5 10.9093L6.30243 10.0955L8.02888 11.8465L12.6895 7.0127L13.5 7.82655Z" fill="black"/>
                            <circle cx="9.5" cy="10.2471" r="9" stroke="#1F1F1F"/>
                        </svg>
                        <span className={`regularLarge ${classes.underlined}`}>Bewertungen: Keine Bewertung</span>
                    </div>
                </div> */}
                <ChatLink 
                    userId={bookingData?.User?.id}
                    studioId={bookingData?.Studio?.id}
                    studioName={bookingData?.Studio?.name}
                    studioImage={bookingData?.Studio?.image}
                    className={`${classes.contactButton} semiboldSmall`}
                    chatRoomId={bookingData?.chatRoomID}
                    bookingId={bookingData?.id}
                >
                    Nachricht schreiben
                </ChatLink>
            </div>
        )}
        {bookedEquipmentCards.length > 0 && (
            <>
                <div className={classes.horizontalLine}></div>
                <div className={classes.paidEquipmentSection}>
                    <h3 className="titleSemibold3">Equipment</h3>
                    <div className={`${dropdownStyles.paidEquipmentViewport} ${classes.equipmentViewportScroll}`}>
                        <div className={`${dropdownStyles.paidEquipmentTrack} ${classes.equipmentViewportScroll}`}>
                            {bookedEquipmentCards.map((item, index) => {
                                const key = item?.equipmentItemId || item?.name || `booking-paid-eq-${index}`;
                                const title = item?.name || "Equipment";
                                const quantity = Number(item?.quantity) || 1;
                                const imageSrc = resolvePaidEquipmentImageSrc(item);
                                return (
                                    <article key={key} className={dropdownStyles.paidEquipmentCard}>
                                        <div className={dropdownStyles.paidEquipmentImageBox}>
                                            {imageSrc ? (
                                                <img src={imageSrc} alt={title} />
                                            ) : (
                                                <span
                                                    className="regularMiddle"
                                                    style={{ color: "#696F76", textAlign: "center" }}
                                                >
                                                    Equipment
                                                </span>
                                            )}
                                        </div>
                                        <h4
                                            className={`${dropdownStyles.paidEquipmentTitle} regularLarge ${classes.equipmentCardTitle}`}
                                        >
                                            {title}
                                        </h4>
                                        <p className={`${dropdownStyles.paidEquipmentPrice} regularSmall`}>
                                            {item?.price != null
                                                ? `${formatPricePerPiece(item.price)} - ${quantity} x gebucht`
                                                : `${quantity} x gebucht`}
                                        </p>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </>
        )}
        {packageData && (
            <>
                <div className={classes.horizontalLine}></div>
                <div className={classes.serviceItemContainer}>
                <h3 className="titleSemibold3">Kreativ-Service</h3>
                <Link to={`/members/${packageData?.creativeID}/services`} className={classes.serviceItem}>
                    <div className={classes.serviceItemImage}>
                        <img 
                            src={fromCreatorPage ? (packageData?.firstPortfolioImage || packageData?.portfolioImages?.[0]) : packageData?.processedPortfolioImages?.[0]} 
                            alt={packageData?.name} 
                        />
                    </div>
                    <div className={classes.serviceItemText}>
                        <span className="semiboldMiddle">{packageData?.name}</span>
                        <span className="regularMiddle">{packageData?.pricePerHour || packageData?.price} € pro Stunde</span>
                        <span style={{color: "var(--dark-grey-4)", marginBottom: "12px"}} className="regularXs">
                            {fromCreatorPage 
                                ? "Der Gast zahlt die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)"
                                : "Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)"
                            }
                        </span>
                        <span className={`regularMiddle ${classes.serviceDescription}`}>{packageData?.description}</span>
                        {packageData?.deliverables?.[0] && (
                            <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                                    <g clipPath="url(#clip0_15597_28902)">
                                        <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                        <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                        <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                        <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_15597_28902">
                                            <rect width="21.8118" height="15.0954" fill="white"/>
                                        </clipPath>
                                    </defs>
                                </svg>
                                <span style={{color: "var(--dark-grey)"}} className="regularXss">{packageData?.deliverables[0]}</span>
                            </div>
                        )}
                    </div>
                </Link>
                </div>
            </>
        )}
        {fromCreatorPage && (
            <>
                <div className={classes.horizontalLine}></div>
                <div className={classes.cancellationContainer}>
                    <h2 className={`${classes.cancellationTitle} titleSemibold3`}>Über das Projekt</h2>
                    <h3 className='regularLarge'>{bookingData?.projectName}</h3>
                    <p onClick={toggleProjectExpand} className={classes.cancellationText}>
                        {bookingText}
                    </p>
                    {(bookingData?.projectDescription?.length > (isMobile ? 90 : 150)) && (
                        <button onClick={toggleProjectExpand} className={`${classes.readMoreButton} semiboldSmall`}>
                            {isProjectExpanded ? 'Weniger anzeigen' : 'Weiterlesen'}
                        </button>
                    )}
                </div>
            </>
        )}
        {cancellationPolicyRaw && (
        <div className={classes.horizontalLine}></div>
        )}
        {cancellationPolicyRaw && (
            <div className={classes.cancellationContainer}>
                <h2 className={`${classes.cancellationTitle} titleSemibold3`}>Stornierungsbedingungen</h2>
                {cancellationPolicyConfig ? (
                    <>
                        <h3 style={{fontSize: "1.125rem"}} className='mediumLarge'>{cancellationPolicyConfig.title}</h3>
                        {cancellationPolicyConfig.lines.map((line) => (
                            <p key={line.prefix} className={classes.cancellationText}>
                                {line.prefix}
                                <br />
                                <span className='semiboldSmall'>{line.highlight}</span>
                            </p>
                        ))}
                    </>
                ) : (
                    <>
                        <p onClick={toggleExpand} className={classes.cancellationText }>
                            {text}
                        </p>
                        {(bookingData?.Studio?.cancellationPolicy?.length > (isMobile ? 90 : 150)) && (
                            <button onClick={toggleExpand} className={`${classes.readMoreButton} semiboldSmall`}>
                                {isExpanded ? 'Weniger anzeigen' : 'Weiterlesen'}
                            </button>
                        )}
                    </>
                )}
            </div>
        )}
        {!fromCreatorPage && (
            <>
                <div className={classes.horizontalLine}></div>
                {houseRulesList.length > 0 && (
                    <div className={classes.cancellationContainer}>
                        <h2 className={`${classes.cancellationTitle} titleSemibold3`}>Grundregeln</h2>
                        <ul onClick={toggleRules} className={classes.rulesList}>
                            {visibleRules.map((rule, index) => (
                                <li className='regularLarge' key={index}>{rule}</li>
                            ))}
                        </ul>
                        {houseRulesList.length > 2 && (
                            <button 
                                className={`${classes.readMoreButton} semiboldSmall`}
                                onClick={toggleRules}
                            >
                                {isRulesExpanded ? 'Weniger anzeigen' : 'Weiterlesen'}
                            </button>
                        )}
                    </div>
                )}
                <div className={classes.horizontalLine}></div>
                <div className={classes.cancellationContainer}>
                    <h2 className={`${classes.cancellationTitle} titleSemibold3`}>Deine Projekt-Beschreibung </h2>
                    <p onClick={toggleBookingExpand} className={classes.cancellationText }>
                        {bookingText}
                    </p>
                    {(bookingData?.projectDescription?.length > (isMobile ? 90 : 150)) && (
                        <button onClick={toggleBookingExpand} className={`${classes.readMoreButton} semiboldSmall`}>
                            {isBookingExpanded ? 'Weniger anzeigen' : 'Weiterlesen'}
                        </button>
                    )}
                </div>
                <div className={classes.horizontalLine}></div>  
          </>
        )}
        {!fromCreatorPage && (
            <div className={`${classes.priceContainer} ${isPriceExpanded ? classes.expanded : ''}`}>
                {isPriceExpanded && (() => {
                    const duration = calculateBookingDuration(bookingData?.startDateTime, bookingData?.endDateTime);
                    const baseStudioPrice = calculateBaseStudioPrice();
                    const totalStudioPrice = Number(bookingData?.totalStudioPrice) || 0;
                    const appliedDiscount = Number(bookingData?.appliedDiscount) || 0;
                    const appliedSurcharge = Number(bookingData?.appliedSurcharge) || 0;
                    const appliedFees = Array.isArray(bookingData?.appliedFees) ? bookingData.appliedFees : [];
                    const totalEquipmentPrice = Number(bookingData?.totalEquipmentPrice) || 0;
                    const creativePackages = Array.isArray(bookingData?.creativePackages) ? bookingData.creativePackages : [];
                    const bookedEquipment = Array.isArray(bookingData?.bookedEquipment) ? bookingData.bookedEquipment : [];
                    const hasCompleteEquipmentPricing = bookedEquipment.length > 0
                        && bookedEquipment.every(
                            (item) => getBookedEquipmentUnitPrice(item?.equipmentItemId) != null
                        );

                    const effectiveBaseStudioPrice = baseStudioPrice != null ? baseStudioPrice : totalStudioPrice;
                    const studioHourly = duration > 0 && effectiveBaseStudioPrice
                        ? Math.round((effectiveBaseStudioPrice / duration) * 100) / 100
                        : bookingData?.Studio?.pricePerHour || null;

                    // Try to derive a more descriptive discount label from studio.discountRules
                    const discountRules = bookingData?.Studio?.discountRules || [];
                    let discountLabel = 'Rabatt';
                    if (appliedDiscount > 0 && discountRules.length && totalStudioPrice > 0) {
                        const baseForRule = totalStudioPrice + appliedDiscount; // base before discount
                        const matchingRule = discountRules.find((rule) => {
                            const minHours = Number(rule?.minHours) || 0;
                            if (duration < minHours) return false;
                            const value = Number(rule?.discountValue) || 0;
                            if (!value) return false;
                            let expected = 0;
                            if (rule?.discountType === 'PERCENTAGE') {
                                expected = Math.round((baseForRule * value) / 100);
                            } else if (rule?.discountType === 'ABSOLUTE') {
                                expected = value;
                            } else {
                                return false;
                            }
                            return Math.abs(expected - appliedDiscount) <= 1; // allow small rounding diff
                        });

                        if (matchingRule) {
                            const value = Number(matchingRule.discountValue) || 0;
                            const minHours = Number(matchingRule.minHours) || 0;
                            const valueLabel =
                                matchingRule.discountType === 'PERCENTAGE'
                                    ? `${value}%`
                                    : `${formatPrice(value)} €`;
                            discountLabel = `Rabatt (${valueLabel}${minHours ? ` ab ${minHours} Stunden` : ''})`;
                        }
                    }

                    return (
                        <div className={classes.priceBreakdown}>
                            {effectiveBaseStudioPrice > 0 && (
                                <div className={classes.breakdownItem}>
                                    <span className="regularLarge">
                                        {bookingData?.packageName
                                            ? 'Paketpreis'
                                            : studioHourly
                                                ? `${studioHourly} € x ${duration} Stunden`
                                                : `€${bookingData?.Studio?.pricePerHour} x ${formatBookingDuration(bookingData?.startDateTime, bookingData?.endDateTime)}`
                                        }
                                    </span>
                                    <span style={{whiteSpace: 'nowrap'}} className="regularLarge">
                                        €{formatPrice(effectiveBaseStudioPrice)}
                                    </span>
                                </div>
                            )}

                            {appliedDiscount > 0 && (
                                <div className={classes.breakdownItem}>
                                    <span className="regularLarge">{discountLabel}</span>
                                    <span style={{whiteSpace: 'nowrap'}} className="regularLarge">-€{formatPrice(appliedDiscount)}</span>
                                </div>
                            )}

                            {appliedSurcharge > 0 && (
                                <div className={classes.breakdownItem}>
                                    <span className="regularLarge">Wochenendaufschlag</span>
                                    <span style={{whiteSpace: 'nowrap'}} className="regularLarge">+€{formatPrice(appliedSurcharge)}</span>
                                </div>
                            )}

                            {appliedFees.map((fee, index) => (
                                <div className={classes.breakdownItem} key={fee?.name || index}>
                                    <span className="regularLarge">{fee?.name || 'Gebühr'}</span>
                                    <span style={{whiteSpace: 'nowrap'}} className="regularLarge">
                                        +€{formatPrice(Number(fee?.amount) || 0)}
                                    </span>
                                </div>
                            ))}

                            {/* Creative packages: price is already total, quantity is typically hours */}
                            {creativePackages.map((pkg, index) => {
                                const quantity = Number(pkg?.quantity) || 0;
                                const totalPrice = Number(pkg?.price) || 0;

                                if (!totalPrice) return null;

                                const unitPrice = quantity > 0 ? totalPrice / quantity : null;

                                return (
                                    <div className={classes.breakdownItem} key={pkg?.packageID || pkg?.packageName || index}>
                                        <span className="regularLarge">
                                            {pkg?.packageName || 'Creative-Paket'}
                                            {unitPrice
                                                ? `: ${formatPrice(unitPrice)} € x ${quantity} Stunden`
                                                : ''}
                                        </span>
                                        <span style={{whiteSpace: 'nowrap'}} className="regularLarge">
                                            +€{formatPrice(totalPrice)}
                                        </span>
                                    </div>
                                );
                            })}

                            {bookedEquipment.length > 0 && (
                                <>
                                    {bookedEquipment.map((item, index) => (
                                        <div className={classes.breakdownItem} key={item?.equipmentItemId || item?.name || index}>
                                            <span className="regularLarge">
                                                {item?.name || 'Equipment'} x {item?.quantity || 1}
                                            </span>
                                            {(() => {
                                                const quantity = Number(item?.quantity) || 1;
                                                const unitPrice = getBookedEquipmentUnitPrice(item?.equipmentItemId);
                                                if (unitPrice == null) return null;
                                                return (
                                                    <span style={{whiteSpace: 'nowrap'}} className="regularLarge">
                                                        +€{formatPrice(unitPrice * quantity)}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                    ))}
                                    {totalEquipmentPrice > 0 && !hasCompleteEquipmentPricing && (
                                        <div className={classes.breakdownItem}>
                                            <span className="regularLarge">Equipment gesamt</span>
                                            <span style={{whiteSpace: 'nowrap'}} className="regularLarge">
                                                +€{formatPrice(totalEquipmentPrice)}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })()}
                <div className={classes.priceMainContent}>
                    <div className={classes.priceInnerContainer}>
                        <h2 style={{marginBottom: "0px"}} className={`${classes.cancellationTitle} titleSemibold3`}>Gesamtbetrag</h2>
                        <button onClick={togglePriceExpand} className={classes.priceDetails}>
                            {isPriceExpanded ? 'Weniger anzeigen' : 'Details sehen'}
                        </button>
                    </div>
                    <p className={`${classes.price} titleSemibold3`}>€{bookingData?.totalPrice}</p>
                </div>
            </div>
        )}
        {(bookingStatus === "CONFIRMED" || bookingStatus === "PENDING") && (
        <div className={classes.horizontalLine}></div>
        )}
        <footer className={`${classes.infoContainer} ${classes.footerButtonsPhone}`}>
            {showCancelBookingAction && (
                <div className={classes.footerInnerContainer}>
                    {/* <button className={`${classes.footerButton} semiboldSmall`}>Änderung anfragen</button> */}
                    {/* <div className={classes.smallLine}></div>  */}
                    <button  
                        onClick={openCancelBookingModal}
                        className={`${classes.footerButton} semiboldSmall`}
                    >
                        {isGuestPendingWithdrawal ? 'Anfrage zurückziehen' : 'Stornieren'}
                    </button>
                </div>
            )}
            <Link to="/kontakt" className={`${classes.supportButton} semiboldSmall`}>Kundensupport anfragen</Link>
        </footer>
        {fromCreatorPage && bookingStatus === "PENDING" && !isBookingTimePassed && (
        <>
            <div className={classes.fixedFooter}>
                <div className={classes.buttonsContainer}>
                    <button 
                        style={{maxWidth: '50%'}}
                        onClick={() => handleOpenModal('reject')}  
                        className={`${classes.button} mediumLarge`}
                    >
                        Ablehnen
                    </button>
                        <button 
                            onClick={() => handleOpenModal('accept')} 
                            style={{backgroundColor: '#000', color: '#fff'}} 
                            className={`${classes.button} mediumLarge`}
                        >
                            Akzeptieren
                        </button>
                </div>
            </div>
            {modalType !== 'cancelBooking' && (
                <SmallModal 
                    fromBookingDetails={true} 
                    open={open} 
                    onBackdropClick={isBookingUpdateLoading ? undefined : handleClose} 
                    className={classes.smallModal}
                >
                {modalType === 'reject'  ? (
                    <div className={classes.modalContent}>
                        <h2 className='titleSemibold3'>Buchung ablehnen?</h2>
                        <div 
                            onClick={isBookingUpdateLoading ? undefined : handleClose} 
                            className={classes.closeButton}
                            style={{ cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer', opacity: isBookingUpdateLoading ? 0.5 : 1 }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                            </svg>
                        </div>
                        <p className={classes.modalText}>Dieser Vorgang kann nicht mehr rückgängig gemacht werden.</p>
                        <div className={classes.modalButtons}>
                            <button 
                                className="mediumLarge" 
                                onClick={handleReject}
                                disabled={isBookingUpdateLoading}
                                style={{
                                    backgroundColor: isBookingUpdateLoading ? 'var(--light-grey-2)' : 'var(--black)', 
                                    color: isBookingUpdateLoading ? 'var(--dark-grey)' : 'var(--white)',
                                    cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer',
                                    opacity: isBookingUpdateLoading ? 0.6 : 1
                                }}
                            >
                                {isBookingUpdateLoading ? 'Wird abgelehnt...' : 'Buchung ablehnen'}
                            </button>
                            <button 
                                className="mediumLarge" 
                                onClick={handleClose}
                                disabled={isBookingUpdateLoading}
                                style={{backgroundColor: 'var(--light-grey-2)', border: '1px solid var(--light-grey-1)', color: 'var(--dark-grey)'}}
                            >
                                Zurück
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className={classes.modalContent}>
                        <h2 className='titleSemibold3'>Buchung akzeptieren</h2>
                        <div 
                            onClick={isBookingUpdateLoading ? undefined : handleClose} 
                            className={classes.closeButton}
                            style={{ cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer', opacity: isBookingUpdateLoading ? 0.5 : 1 }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                            </svg>
                        </div>
                        <p className={classes.modalText}>Mit dieser Auswahl bestätigst du diese Buchung.</p>
                        <div className={classes.modalButtons}>
                            <button 
                                className="mediumLarge" 
                                onClick={handleAccept}
                                disabled={isBookingUpdateLoading}
                                style={{
                                    backgroundColor: isBookingUpdateLoading ? 'var(--light-grey-2)' : 'var(--black)', 
                                    color: isBookingUpdateLoading ? 'var(--dark-grey)' : 'var(--white)',
                                    cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer',
                                    opacity: isBookingUpdateLoading ? 0.6 : 1
                                }}
                            >
                                {isBookingUpdateLoading ? 'Wird akzeptiert...' : 'Buchung akzeptieren'}
                            </button>
                            <button 
                                className="mediumLarge" 
                                onClick={handleClose}
                                disabled={isBookingUpdateLoading}
                                style={{backgroundColor: 'var(--light-grey-2)', border: '1px solid var(--light-grey-1)', color: 'var(--dark-grey)'}}
                            >
                                Zurück
                            </button>
                        </div>
                    </div>
                )}
                </SmallModal>
            )}
        </>
        )}
        {/* {!fromCreatorPage && !isRateBookingOpen && !isMobile && modalType !== 'cancelBooking' && (
        <SmallModal 
            open={open}
            onBackdropClick={handleClose} 
            className={`${classes.reviewsModal} ${classes.fitContent}`} 
        > 
            <SmallModalGuestContent />
        </SmallModal>
        )} */}
        {/* {!fromCreatorPage && isRateBookingOpen && !isMobile && modalType !== 'cancelBooking' && (
        <SmallModal open={open} onBackdropClick={handleClose} className={classes.reviewsModal}>
            <SmallModalBookingContent />
        </SmallModal>
        )} */}
        {modalType === 'cancelBooking' && (
            <SmallModal 
                fromBookingDetails={true} 
                open={open} 
                onBackdropClick={isBookingUpdateLoading ? undefined : handleClose} 
                className={classes.deleteModal}
            >
                <div className={classes.modalContent}>
                    {(() => {
                        const isWithdrawal = !fromCreatorPage && bookingStatus === "PENDING";
                        const isHostPendingDecline = fromCreatorPage && bookingStatus === "PENDING";
                        return (
                            <>
                                <h2 className='titleSemibold3'>
                                    {isWithdrawal
                                        ? 'Anfrage zurückziehen?'
                                        : isHostPendingDecline
                                            ? 'Buchung ablehnen?'
                                            : 'Buchung stornieren?'}
                                </h2>
                                <div 
                                    onClick={isBookingUpdateLoading ? undefined : handleClose} 
                                    className={classes.closeButton}
                                    style={{ cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer', opacity: isBookingUpdateLoading ? 0.5 : 1 }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                                    </svg>
                                </div>
                                <p className={classes.modalText}>
                                    {isWithdrawal 
                                        ? 'Mit dieser Auswahl ziehst du deine Buchungsanfrage zurück.'
                                        : isHostPendingDecline
                                            ? 'Mit dieser Auswahl lehnst du diese Buchungsanfrage ab.'
                                        : 'Mit dieser Auswahl stornierst du diese Buchung.'}
                                </p>
                                <div className={classes.modalButtons}>
                                    <button 
                                        className="mediumLarge" 
                                        onClick={handleCancelBooking}
                                        disabled={isBookingUpdateLoading}
                                        style={{
                                            backgroundColor: isBookingUpdateLoading ? 'var(--light-grey-2)' : 'var(--black)', 
                                            color: isBookingUpdateLoading ? 'var(--dark-grey)' : 'var(--white)',
                                            cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer',
                                            opacity: isBookingUpdateLoading ? 0.6 : 1
                                        }}
                                    >
                                        {isBookingUpdateLoading 
                                            ? (isWithdrawal ? 'Wird zurückgezogen...' : isHostPendingDecline ? 'Wird abgelehnt...' : 'Wird storniert...')
                                            : (isWithdrawal ? 'Anfrage zurückziehen' : isHostPendingDecline ? 'Buchung ablehnen' : 'Buchung stornieren')}
                                    </button>
                                    <button 
                                        className="mediumLarge" 
                                        onClick={handleClose}
                                        disabled={isBookingUpdateLoading}
                                        style={{backgroundColor: 'var(--light-grey-2)', border: '1px solid var(--light-grey-1)', color: 'var(--dark-grey)'}}
                                    >
                                        Abbrechen
                                    </button>
                                </div>
                            </>
                        );
                    })()}
                </div>
            </SmallModal>
        )}
        {/* {!fromCreatorPage && isRateGuestOpen && isMobile && (
            <div className={classes.mobileModal}>
                <div className={classes.mobileModalContent}>
                    <header className={classes.mobileRateGuestHeader}>
                        <button onClick={handleClose}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                            </svg>
                        </button>
                        <h2 className="mediumLarge">Bewerte deinen Host</h2>
                    </header>
                    {currentPage === 1 ? (
                    <div className={classes.mobileRateGuestContent}>
                        <div className={classes.userContainer}>
                            <div className={classes.userImageContainer}>
                                <img className={classes.userImageBookingRating} src={bookingData?.Studio?.User?.studioOwnerProfilePicture} alt="user" />
                            </div>
                            <div className={classes.userInfoContainer}>
                                <h2 className="titleSemibold2">{bookingData?.Studio?.User?.firstName} {bookingData?.Studio?.User?.lastName}</h2>
                                <p className="regularXs">{coarseStudioLocation}</p>
                            </div>
                        </div>
                        <div className={classes.startReviewContainer}>
                            <h2 className="titleSemibold3">Bewerte deinen Host.</h2>
                            <div className={classes.starsContainer}>
                                    {[...Array(5)].map((_, index) => (
                                        <svg 
                                            key={index}
                                            onClick={() => handleStarClick(index)}
                                            style={{cursor: 'pointer'}}
                                            xmlns="http://www.w3.org/2000/svg" 
                                            width="42" 
                                            height="40" 
                                            viewBox="0 0 42 40"
                                            fill="none"
                                        >
                                            <g clipPath="url(#clip0_10926_15309)">
                                                <path d="M32.2487 29.5767L33.9688 39.6587L20.9961 32.8737L31.508 25.3003L32.0097 28.1911L32.2487 29.5767Z" fill={index < selectedStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
                                                <path d="M9.98635 28.1911L10.4881 25.3003L0 15.1468L14.5017 13.0444L21 0L27.4983 13.0444L42 15.1468L8.0273 39.6587L9.98635 28.1911Z" fill={index < selectedStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_10926_15309">
                                                    <rect width="42" height="39.6587" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    ))}
                            </div>
                        </div>
                        <div className={classes.reviewsInnerContainerSecond}>
                            <div className={classes.reviewsInnerContainerSecondHeader}>
                                <h2 className="titleSemibold3">Teile deine Erfahrung mit {bookingData?.Studio?.User?.firstName}</h2>
                                <h2 className="titleSemibold3">(optional)</h2>
                            </div>
                            <textarea 
                                className={`${classes.reviewTextarea} slimRegularLarge`} 
                                placeholder={`Beschreibe deine Erfahrung mit ${bookingData?.Studio?.User?.firstName} hier...`}
                                defaultValue={guestReviewText}
                                onBlur={(e) => setGuestReviewText(e.target.value)}
                            ></textarea>
                        </div>
                    </div>
                    ) : (
                    <div className={classes.successContentContainer}>
                        <div className={classes.successContent}>
                            <div className={classes.successContentIcon}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M31.6074 2.63723L9.69105 23.4831L9.69062 23.4827L9.68855 23.4847L0.392578 13.9887L2.48517 11.9402L9.76228 19.3739L29.5892 0.515381L31.6074 2.63723Z" fill="white"/>
                                </svg>
                            </div>
                            <h2 className={`${classes.successContentTitle} titleSemibold2`}>Deine Bewertung wurde abgeschickt!</h2>
                        </div>
                        <div className={classes.successContentText}>
                            <p className="regularLarge">Danke für dein Feedback.</p>
                        </div>
                    </div>
                    )}
                    <div className={classes.mobileRateGuestNextButtonContainer}>
                        <button 
                            className={`${classes.mobileRateGuestNextButton} mediumLarge`}
                            onClick={currentPage === 2 ? submitReview : currentPage === 3 ? handleClose : handleNextClick}
                            disabled={(currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting}
                            style={{
                                backgroundColor: (currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting ? 'var(--light-grey-2)' : 'var(--light-black)',
                                color: (currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting ? 'var(--grey-2)' : 'var(--white)',
                                opacity: (currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting ? 0.5 : 1,
                                cursor: (currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {currentPage === 1 ? 'Abschicken' : currentPage === 2 ? (isUserReviewSubmitting ? 'Wird gesendet...' : 'Abschicken') : 'Fertig'}
                        </button>
                    </div>
                </div>
            </div>
        )} */}
        {/* {!fromCreatorPage && isRateBookingOpen && isMobile && (
            <div className={classes.mobileModal}>
                <SmallModalBookingContent />
            </div>
        )} */}
    </div>
    <SaveNotification 
        show={showNotification} 
        onHide={() => setShowNotification(false)}
        message={notificationMessage}
    />
    </>
  );
};

export default BookingDetailsMain;
